<script>
    import {PATH_PREFIX} from '../../../apiUrls.js';

    import { submit } from '../../../utils.js';
    import { sgettext } from '../../../i18n.js';
    import { storeRows, storeCreating } from './store.js';
    import { elementDetails, draftElementDetails, elementInstance } from '../../Workspaces/workspacesStore.js';
    import { get } from '../../../utils.js';
    import MegaSelect from '../../MegaSelect/MegaSelect.svelte';
    import ConfirmationModal from '../../ConfirmationModal.svelte';
    import Button from '../../Button.svelte';

    // the data of the saved item
    export let data = {};

    // variables
    export let branchId = 0;
    export let activityVersionId = 0;
    export let elementVersionId = 0;
    export let elementId = 0;
    export let eevId = 0;

    export let mechanismListPlacement = 'auto';
    export let mechanismListAbsoluteParentId = undefined;
    export let mechanismListAbsoluteScrollParentSelector = undefined;

    // selected values
    let energyType = null;
    export let mechanism = null;
    export let hazard = null;
    export let control = null;

    // status flags and variables
    export let isCreating = false;
    export let isEditing = false;
    export let waiting = false;
    let error = null;

    // options of MegaSelect lists
    export let energyTypes = [];
    let hazards = [];
    let controls = [];

    // form validation
    let validForm = false;
    $: validForm = mechanism && hazard && control;

    // set HTML element ids
    let mechanismId = 'newMechanism';
    let hazardId = 'newHazard';
    let controlId = 'newControl';
    let deleteConfirmationId = 'deleteMechanismHazardControlRow';

    let draftElementVersionViewApiUrl =
            `${PATH_PREFIX}/sw/api/elements_database/draft_element_version/`;

    $: {
        if ('id' in data) {
            mechanismId = `${data.id}-mechanism`;
            hazardId = `${data.id}-hazard`;
            controlId = `${data.id}-control`;
            deleteConfirmationId = `${data.id}-deleteMechanismHazardControlRow`;
        }
    }

    // set MegaSelect noOptionMessage variable
    let hazardNoOptionsMessage = sgettext('$$RESULTS_FOUND$$');
    let controlNoOptionsMessage = sgettext('$$RESULTS_FOUND$$');
    $: {
        if (!mechanism) {
            hazardNoOptionsMessage = sgettext('$$PLEASE_SELECT_MECHANISM_FIRST$$');
        } else {
            hazardNoOptionsMessage = sgettext('$$RESULTS_FOUND$$');
        }

        if (!mechanism && !hazard) {
            controlNoOptionsMessage = sgettext('$$PLEASE_SELECT_MECHANISM_RISK_FIRST$$');
        } else if (!hazard) {
            controlNoOptionsMessage = sgettext('$$PLEASE_SELECT_RISK_FIRST$$');
        } else {
            controlNoOptionsMessage = sgettext('$$RESULTS_FOUND$$');
        }
    }

    // APIs
    let saveApi = `${PATH_PREFIX}/sw/api/elementhazardcontrol/`;
    let deleteApi = `${PATH_PREFIX}/sw/api/kendo/element_version/{0}/element_hazard_and_control/remove/`;


    // functions to populate initial values and options

    let getAllHazardsFromOptions = options => {
        let result = [];
        for (let oEnergyType of options) {
            for (let oMechanism of oEnergyType.mechanisms) {
                for (let oHazard of oMechanism.global_hazards) {
                    let aHazard = {...oHazard};
                    aHazard.energyType = {...oEnergyType};
                    aHazard.mechanism = {...oMechanism};
                    result.push({...aHazard});
                }
            }
        }
        return result;
    };

    let populateInitValues = (values, options) => {
        if (options.length && values?.id && values?.mechanism?.id) {
            // get initial values from the saved data
            let found = false;
            for (let option of options) {
                for (let mechanismOption of option.mechanisms) {
                    if (mechanismOption.id == values.mechanism.id) {
                        mechanism = {...mechanismOption};
                        energyType = values.mechanism ? {...values.mechanism.energy_type} : null;
                        hazards = mechanism ? [...mechanism.global_hazards] : [];
                        if (values.global_hazard_id) {
                            for (let hazardOption of mechanismOption.global_hazards) {
                                if (hazardOption.id == values.global_hazard_id) {
                                    hazard = {...hazardOption};
                                    controls = hazard ? [...hazard.global_controls] : [];
                                    if (values.global_control_id) {
                                        for (let controlOption of hazardOption.global_controls) {
                                            if (controlOption.id == values.global_control_id) {
                                                control = {...controlOption};
                                                break;
                                            }
                                        }
                                    }
                                    break;
                                }
                            }
                        }
                        found = true;
                        break;
                    }
                }
                if (found) break;
            }
        } else if (options.length) {
            // try to auto-populate Mechanism, Hazard (Risk) and Control,
            // if there is only one option
            let mechanismsCount = 0;
            let aMechanism = null;
            let aEnergyType = null;
            for (let option of options) {
                if (option.mechanisms.length == 1) {
                    mechanismsCount++;
                    aMechanism = {...option.mechanisms[0]};
                    aEnergyType = {...option};
                } else if (option.mechanisms.length > 1 || mechanismsCount > 1) {
                    mechanismsCount = 0;
                    break;
                }
            }

            if (mechanismsCount === 1)
                doSelectMechanism(aMechanism, aEnergyType);
        }
    };

    $: {
        populateInitValues(data, energyTypes);
        hazards = getAllHazardsFromOptions(energyTypes);
    }

    // handlers

    async function save() {
        if (!hazard || !control) return;

        let payload = {
            element_version: elementVersionId,
            global_hazard_id: hazard.id,
            global_control_id: control.id
        }
        if (data.id) payload.id = data.id;

        let response = {};
        error = null;
        waiting = true;

        if (payload.id) {
            const url = `${saveApi}${payload.id}/`;
            response = await submit(url, 'PATCH', payload);
        } else {
            const url = `${saveApi}?ev_id=${elementVersionId}&activity_version_id=${activityVersionId}`;
            response = await submit(url, 'POST', payload);
        }

        waiting = false;

        if (response.ok) {
            const result = await response.json();
            data = result;
            if (isEditing) {
                isEditing = false;
            } else if (isCreating) {
                isCreating = false
                storeCreating.set(false);
                storeRows.set([...$storeRows, data]);
            }
            await getElementVersionDetails()
        } else {
            const result = await response.json();
            error = result?.non_field_errors ? result.non_field_errors : null;
        }
    }

    let cancelCreating = () => {
        isCreating = false;
        error = null;
        storeCreating.set(false);
    };

    let cancelEditing = () => {
        isEditing = false;
        error = null;
        populateInitValues(data, energyTypes);
    };

    let showDeleteModal = () => {
        jQuery(`#${deleteConfirmationId}`).modal('show');
    };

    let hideDeleteModal = () => {
        jQuery(`#${deleteConfirmationId}`).modal('hide');
    };

    async function handleDelete() {
        waiting = true;

        const response = await submit(
            deleteApi.replace('{0}', elementVersionId),
            'POST',
            {
                id: data.id,
                activity_version_id: activityVersionId
            }
        );

        if (response.ok) {
            hideDeleteModal();
            $storeRows = $storeRows.filter((value, index, arr) => {
                if (value.id !== data.id) return value;
            });
            await getElementVersionDetails();
        }

        waiting = false;
    }

    let isObjectIdChanged = (oldObject, newObject) => {
        return newObject?.id !== oldObject?.id;
    };

    let doSelectControl = newControl => {
        control = {...newControl};
        validForm = mechanism && hazard && control;
    };

    let doSelectHazard = newHazard => {
        hazard = {...newHazard};
        if (hazard?.energyType) energyType = {...hazard.energyType};
        if (hazard?.mechanism) mechanism = {...hazard.mechanism};
        control = null;
        controls = [...hazard.global_controls];

        if (controls.length == 1) doSelectControl(controls[0]);
    };

    let doSelectMechanism = (newMechanism, newEnergyType=null) => {
        mechanism = {...newMechanism};
        hazard = null;
        control = null;
        hazards = [...mechanism.global_hazards];
        controls = [];

        if (hazards.length == 1) doSelectHazard(hazards[0]);

        if (newEnergyType) energyType = {...newEnergyType};
    };

    let handleSelectMechanism = (event, item) => {
        if (isObjectIdChanged(mechanism, event.detail)) {
            if (!isCreating) isEditing = true;
            doSelectMechanism(event.detail)
        }
    };

    let handleClearMechanism = event => {
        if (!isCreating) isEditing = true;
        error = null;
        mechanism = null;
        hazard = null;
        control = null;
        hazards = getAllHazardsFromOptions(energyTypes);
        controls = [];
    };

    let handleSelectHazard = event => {
        if (isObjectIdChanged(hazard, event.detail)) {
            if (!isCreating) isEditing = true;
            doSelectHazard(event.detail);
        }
    };

    let handleClearHazard = event => {
        if (!isCreating) isEditing = true;
        error = null;
        hazard = null;
        control = null;
        controls = [];
    };

    let handleSelectControl = event => {
        if (isObjectIdChanged(control, event.detail)) {
            if (!isCreating) isEditing = true;
            doSelectControl(event.detail);
        }
    };

    let handleClearControl = event => {
        if (!isCreating) isEditing = true;
        error = null;
        control = null;
    };

    async function getElementVersionDetails() {
        const elementId = $elementDetails ? $elementDetails.elementId : 0;
        if (elementId) {
            const elementInstanceParam = $elementInstance.id ? `?elementInstanceId=${$elementInstance.id}` : '';
            const resp = await get(`${draftElementVersionViewApiUrl}${elementId}/${elementInstanceParam}`);
            draftElementDetails.set(null);
            if (resp.ok) {
                const json = await resp.json();
                let details = json;
                details.competencies = json.competencies.map((c) => ({
                    id: c.competency,
                    name: c.name,
                }));
                draftElementDetails.set(details);
            }
        }
    }
</script>

<tr class:border-red="{data.invalid_hazard_control}">
    <td>
        {#if data.invalid_hazard_control}
            <i class='fa fa-exclamation-circle text-danger' title={`(${data.global_hazard.name}, ${data.global_control.name}) ` + sgettext('$$NOT_VALID_RISK_CONTROL_COMBINATION$$')}></i>
        {/if}
    </td>
    <td class='field'>
        <MegaSelect
            id={mechanismId}
            options={energyTypes}
            headers={[
                sgettext('$$HAZARDOUS_ENERGY$$'),
                sgettext('$$WHAT_1$$'),
                sgettext('$$SELECT_MECHANISM_1$$')
            ]}
            placeholder={sgettext('$$SEARCHSELECT_MECHANISM_$$')}
            noOptionsMessage={sgettext('$$RESULTS_FOUND$$')}
            isListGrouped={true}
            showImages={true}
            listPlacement={mechanismListPlacement}
            listAbsoluteParentId={mechanismListAbsoluteParentId}
            listAbsoluteScrollParentSelector={mechanismListAbsoluteScrollParentSelector}
            itemValueKey='id'
            itemLabelKey='name'
            groupLabelKey='name'
            groupDescriptionKey='description'
            getGroupImageUrl={group => group.icon ? group.icon.url : ''}
            groupItemsKey='mechanisms'
            selectedItem={mechanism}
            bind:groupOfSelectedItem={energyType}
            onSelectItem={handleSelectMechanism}
            onClear={handleClearMechanism}
            readOnly={waiting}
            isWaiting={waiting}
            clearable={true}
        />
    </td>
    <td class='field'>
        <MegaSelect
            id={hazardId}
            options={hazards}
            placeholder={sgettext('$$SEARCHSELECT_RISK_$$')}
            noOptionsMessage={hazardNoOptionsMessage}
            itemValueKey='id'
            itemLabelKey='name'
            itemDescriptionKey='description'
            showDescriptions={true}
            getItemImageUrl={item => item.icon ? item.icon.url : ''}
            selectedItem={hazard}
            onSelectItem={handleSelectHazard}
            onClear={handleClearHazard}
            readOnly={waiting}
            isWaiting={waiting}
        />
    </td>
    <td class='field'>
        <MegaSelect
            id={controlId}
            options={controls}
            placeholder={sgettext('$$SEARCHSELECT_CONTROL_$$')}
            noOptionsMessage={controlNoOptionsMessage}
            itemValueKey='id'
            itemLabelKey='name'
            itemDescriptionKey='description'
            showDescriptions={true}
            getItemImageUrl={item => item.icon ? item.icon.url : ''}
            selectedItem={control}
            onSelectItem={handleSelectControl}
            onClear={handleClearControl}
            readOnly={waiting}
            isWaiting={waiting}
        />
        {#if error}
            <p class='error'>{error}</p>
        {/if}
    </td>
    <td class='actions d-flex flex-nowrap justify-content-end'>
        {#if isCreating}
            <Button
                type='btn btn-outline-primary mr-1'
                on:click={save}
                disabled={waiting || !validForm}
                sending={waiting}
                name={sgettext('$$ADD_1$$')}
            />
            <Button
                type='btn btn-outline-secondary'
                on:click={cancelCreating}
                disabled={waiting}
                name={sgettext('$$CANCEL$$')}
            />
        {:else if isEditing}
            <Button
                type='btn btn-outline-primary mr-1'
                on:click={save}
                disabled={waiting || !validForm}
                sending={waiting}
                name={sgettext('$$UPDATE_1$$')}
            />
            <Button
                type='btn btn-outline-secondary'
                on:click={cancelEditing}
                disabled={waiting}
                name={sgettext('$$CANCEL$$')}
            />
        {:else}
            <span on:click={showDeleteModal}>
                <i class='fas fa5-trash-alt'/>
            </span>
        {/if}
    </td>
</tr>

<ConfirmationModal
    id={deleteConfirmationId}
    title="{sgettext('$$DELETE_CONFIRMATION$$')}"
    onYes={handleDelete}
    onNo={hideDeleteModal}
    sending={waiting}
>
    <div slot='message'>
        <p>{sgettext('$$SURE_WANT_DELETE_FOLLOWING_RISK_CONTROL$$')}</p>
        <label>{sgettext('$$RISK$$')}</label>
        {#if data.global_hazard && data.global_hazard.name}
            <div>{data.global_hazard.name}</div>
        {/if}
        {#if data.global_hazard && data.global_hazard.description}
            <div>{data.global_hazard.description}</div>
        {/if}
        <br/>
        <label>{sgettext('$$CONTROL$$')}</label>
        {#if data.global_control && data.global_control.name}
            <div>{data.global_control.name}</div>
        {/if}
        {#if data.global_control && data.global_control.description}
            <div>{data.global_control.description}</div>
        {/if}
        <br/>
    </div>
</ConfirmationModal>

<style>
    td {
        min-height: 100px;
    }

    td:first-child {
        padding: 0 0 0 5px;
        margin: 0;
        vertical-align: middle;
        text-align: center;
    }

    .field {
        width: 30%;
        vertical-align: middle;
    }

    .actions {
        text-align: right;
        min-width: 210px;
    }

    .actions i {
        margin: auto 0;
        cursor: pointer;
    }

    .actions i:hover {
        color: #dd5900;
    }

    .actions :global(.btn) {
        margin: auto 10px;
        height: 40px;
    }

    .actions :global(.btn:first-child) {
        margin-left: 0;
    }

    .error {
        color: red;
        margin: 3px 5px;
        font-size: small;
    }

    .border-red {
        border: #ff0000 solid 3px;
    }
</style>
